import type { WorldEventContext, WorldEventHandler } from '../types';
import {
  EventPOIInnRest,
  EventPOITavernRumor,
  EventPOIVillageStop,
} from '../types';
import type {
  WorldEventStartPayload,
  WorldEventLinePayload,
  WorldEventEndPayload,
  RoadPOIConfig,
} from '@/modules/game-protocol/ws-payloads.types';
import { registerWorldEventHandler } from '../registry';
import { adventureLogTemplate, localizedQuestText } from '@/i18n/loadLocales';
import { t } from '@/i18n';

function isRoadPOIEventId(eventId: string): eventId is
  | typeof EventPOIInnRest
  | typeof EventPOITavernRumor
  | typeof EventPOIVillageStop {
  return (
    eventId === EventPOIInnRest ||
    eventId === EventPOITavernRumor ||
    eventId === EventPOIVillageStop
  );
}

function onStart(payload: WorldEventStartPayload, ctx: WorldEventContext): void {
  if (!isRoadPOIEventId(payload.eventId)) return;
  const cfg = (payload.config ?? {}) as RoadPOIConfig;
  ctx.setActive({
    kind: 'roadPoi',
    eventId: payload.eventId,
    sessionId: payload.sessionId,
    actions: Array.isArray(cfg.actions) && cfg.actions.length > 0 ? cfg.actions : ['leave'],
    promptEndsAt: cfg.promptEndsAt,
    durationMs: cfg.durationMs,
  });
}

function onEnd(payload: WorldEventEndPayload, ctx: WorldEventContext): void {
  const active = ctx.getActive();
  if (active?.kind === 'roadPoi' && active.sessionId === payload.sessionId) {
    ctx.setActive(null);
  }
}

function onLine(payload: WorldEventLinePayload, ctx: WorldEventContext): void {
  // These dialogue frames echo the exact journal lines already committed by the server.
  if (payload.lineKey === 'road_poi.visit.enter' || payload.lineKey === 'road_poi.tavern.quest_offer') return;
  const { locale } = ctx.locale();
  let line = '';
  if (payload.lineKey) {
    const tmpl = adventureLogTemplate(locale, payload.lineKey);
    if (payload.lineKey === 'road_poi.tavern.quest_offer') {
      const questKey = (payload.text ?? '').trim();
      const title = localizedQuestText(locale, questKey, 'title', questKey);
      line = tmpl ? t(tmpl, { title }) : title;
    } else if (tmpl) {
      line = tmpl;
    }
  }
  if (!line) {
    line = (payload.text ?? '').trim();
  }
  if (!line) return;
  ctx.appendLogClientMessage(line);
}

const handler: WorldEventHandler = {
  eventId: EventPOIInnRest,
  onStart,
  onLine,
  onEnd,
};

registerWorldEventHandler(handler);
registerWorldEventHandler({ ...handler, eventId: EventPOITavernRumor });
registerWorldEventHandler({ ...handler, eventId: EventPOIVillageStop });

export default handler;
